import { useEffect, useMemo, useState } from "react";
import { Modal } from "newui/components/ui/Modal";
import { InputWithLabel, SelectWithLabel } from "newui/components/ui/InputWithLabel";
import { ToggleButtonGroup } from "newui/components/ui/ToggleButtonGroup";
import { useCloneRole, useCreateRole, useRoles } from "newui/features/RolesPermissions/hooks/useRoles";
import type { RoleResource } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { showToast } from "utils/helpers";

export interface CreateRoleModalProps {
	isOpen: boolean;
	onClose: () => void;
	// Called with the newly-created role so the page can focus it in the matrix.
	onCreated?: (role: RoleResource) => void;
}

type CreateMode = "template" | "scratch";

export const CreateRoleModal = ({ isOpen, onClose, onCreated }: CreateRoleModalProps) => {
	const { data: roles } = useRoles();
	const createRoleMutation = useCreateRole();
	const cloneRoleMutation = useCloneRole();

	const [mode, setMode] = useState<CreateMode>("template");
	const [name, setName] = useState("");
	const [sourceRoleId, setSourceRoleId] = useState<number | null>(null);

	// Only system (base) roles make sensible starting templates.
	const templateRoles = useMemo(
		() => (roles ?? []).filter((role) => role.is_base),
		[roles]
	);
	const templateOptions = templateRoles.map((role) => ({ label: role.name, value: role.id }));
	const selectedTemplateOption =
		templateOptions.find((option) => option.value === sourceRoleId) ?? null;

	// Reset the form each time the modal opens.
	useEffect(() => {
		if (isOpen) {
			setMode("template");
			setName("");
			setSourceRoleId(null);
		}
	}, [isOpen]);

	const isPending = createRoleMutation.isPending || cloneRoleMutation.isPending;
	const trimmedName = name.trim();
	const canSubmit =
		trimmedName.length > 0 && (mode === "scratch" || sourceRoleId !== null) && !isPending;

	const handleClose = () => {
		createRoleMutation.reset();
		cloneRoleMutation.reset();
		onClose();
	};

	const handleSuccess = (role: RoleResource, message: string) => {
		showToast(message, "Role created", true, "create-role-success");
		onCreated?.(role);
		handleClose();
	};

	const handleError = () => {
		showToast("Unable to create this role. The name may already be in use.", "Create failed", false, "create-role-error");
	};

	const handleConfirm = () => {
		if (!canSubmit) {
			return;
		}

		if (mode === "scratch") {
			createRoleMutation.mutate(
				{ name: trimmedName },
				{
					onSuccess: (role) => handleSuccess(role, `"${trimmedName}" created with no permissions yet.`),
					onError: handleError,
				}
			);
			return;
		}

		if (sourceRoleId !== null) {
			cloneRoleMutation.mutate(
				{ source_role_id: sourceRoleId, name: trimmedName },
				{
					onSuccess: (role) => handleSuccess(role, `"${trimmedName}" created from a system role.`),
					onError: handleError,
				}
			);
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title="Create Role"
			description="Start from a system role, or create a role from scratch and set its permissions yourself."
			iconType="create"
			size="md"
			primaryButton={{
				text: isPending ? "Creating..." : "Create Role",
				btntype: "primary",
				disabled: !canSubmit,
				isLoading: isPending,
				onClick: handleConfirm,
			}}
			secondaryButton={{
				text: "Cancel",
				btntype: "secondary",
				disabled: isPending,
				onClick: handleClose,
			}}
		>
			<div className="space-y-4" data-testid="create-role-modal-content">
				<ToggleButtonGroup
					options={[
						{ label: "From a system role", value: "template" },
						{ label: "From scratch", value: "scratch" },
					]}
					value={mode}
					onChange={(value) => setMode(value as CreateMode)}
				/>

				{mode === "template" && (
					<SelectWithLabel
						label="Copy from system role"
						required
						testId="create-role-source"
						options={templateOptions}
						value={selectedTemplateOption}
						onChange={(option) =>
							setSourceRoleId(
								option && !Array.isArray(option) ? (option.value as number) : null
							)
						}
						placeholder="Select a system role…"
					/>
				)}

				<InputWithLabel
					label="New role name"
					value={name}
					onChange={(e) => setName(e.target.value)}
					testId="create-role-name-input"
					required
				/>

				<p className="text-xs text-gray-500">
					{mode === "template"
						? "The new role copies the selected system role's permissions — edit them afterwards in the matrix."
						: "The new role starts with No access to every module. Set its permissions in the matrix, then Save changes."}
				</p>
			</div>
		</Modal>
	);
};
